import React, { useCallback, useEffect, useState } from 'react';
import eventBus from '../EventBus';

type LoadingProps = {};

const LoadingScreen: React.FC<LoadingProps> = () => {
    const [progress, setProgress] = useState(0);
    const [loaded, setLoaded] = useState(0);
    const [toLoad, setToLoad] = useState(0);
    const [overlayOpacity, setLoadingOverlayOpacity] = useState(1);
    const [phase, setPhase] = useState<'terminal' | 'boot' | 'login' | 'done'>('terminal');
    const [terminalLines, setTerminalLines] = useState<string[]>([]);
    const [webGLError, setWebGLError] = useState(false);
    const [webGLErrorOpacity, setWebGLErrorOpacity] = useState(0);
    const [loginVisible, setLoginVisible] = useState(false);
    const [mobileWarning, setMobileWarning] = useState(window.innerWidth < 768);
    const [progressBarWidth, setProgressBarWidth] = useState(0);
    const [cursorVisible, setCursorVisible] = useState(true);

    const onResize = () => {
        setMobileWarning(window.innerWidth < 768);
    };

    window.addEventListener('resize', onResize);

    const detectWebGLContext = () => {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        return !!(gl && gl instanceof WebGLRenderingContext);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('debug')) {
            start();
        } else if (!detectWebGLContext()) {
            setWebGLError(true);
            setTimeout(() => setWebGLErrorOpacity(1), 500);
        }
        animateBootBar();
        runTerminalSequence();

        // Blinking cursor
        const cursorInterval = setInterval(() => {
            setCursorVisible(v => !v);
        }, 530);
        return () => clearInterval(cursorInterval);
    }, []);

    const runTerminalSequence = () => {
        const lines = [
            'Last login: Fri Mar  6 07:23:44 on ttys000',
            '',
            'sujal@MacBook-Pro ~ % system_profiler SPSoftwareDataType',
            '      macOS 14.3.1 (23D60)',
            '      Kernel Version: Darwin 23.3.0',
            '',
            'sujal@MacBook-Pro ~ % ./portfolio --init',
            'Mounting file system... done',
            'Loading shaders... done',
            'Starting 3D renderer...',
        ];

        let i = 0;
        const interval = setInterval(() => {
            if (i < lines.length) {
                setTerminalLines((prev) => [...prev, lines[i]]);
                i++;
            } else {
                clearInterval(interval);
                setTimeout(() => setPhase('boot'), 500);
            }
        }, 260);
    };

    const animateBootBar = () => {
        let val = 0;
        const interval = setInterval(() => {
            val += Math.random() * 3 + 1;
            if (val >= 40) {
                clearInterval(interval);
                setProgressBarWidth(40);
            } else {
                setProgressBarWidth(val);
            }
        }, 60);
    };

    useEffect(() => {
        eventBus.on('loadedSource', (data: any) => {
            setProgress(data.progress);
            setToLoad(data.toLoad);
            setLoaded(data.loaded);
            setProgressBarWidth(40 + data.progress * 60);
        });
    }, []);

    useEffect(() => {
        if (progress >= 1 && phase === 'boot' && !webGLError) {
            setProgressBarWidth(100);
            setTimeout(() => {
                setPhase('login');
                setTimeout(() => setLoginVisible(true), 100);
            }, 800);
        }
    }, [progress, phase, webGLError]);

    const start = useCallback(() => {
        setLoginVisible(false);
        setTimeout(() => {
            setLoadingOverlayOpacity(0);
            eventBus.dispatch('loadingScreenDone', {});
            const ui = document.getElementById('ui');
            if (ui) ui.style.pointerEvents = 'none';
        }, 400);
    }, []);

    return (
        <div style={{ ...styles.overlay, opacity: overlayOpacity, transform: `scale(${overlayOpacity === 0 ? 1.05 : 1})` }}>
            {/* Background */}
            <div style={styles.bgGradient} />

            {/* Terminal Phase — macOS Terminal.app style */}
            {phase === 'terminal' && !webGLError && (
                <div style={styles.terminalWindow}>
                    {/* Terminal window chrome */}
                    <div style={styles.terminalTitlebar}>
                        <div style={styles.trafficLights}>
                            <div style={{ ...styles.tl, background: '#ff5f57' }} />
                            <div style={{ ...styles.tl, background: '#ffbd2e' }} />
                            <div style={{ ...styles.tl, background: '#28c840' }} />
                        </div>
                        <span style={styles.terminalTitle}>zsh — 80×24</span>
                    </div>
                    {/* Terminal body */}
                    <div style={styles.terminalBody}>
                        {terminalLines.map((line, idx) => (
                            <div key={idx} style={line === '' ? styles.emptyLine : styles.terminalLine}>
                                {line || '\u00A0'}
                            </div>
                        ))}
                        {/* Blinking block cursor */}
                        <div style={styles.terminalPromptRow}>
                            <span style={styles.promptUser}>sujal@MacBook-Pro</span>
                            <span style={styles.promptDir}> ~ % </span>
                            <span style={{ ...styles.terminalCursor, opacity: cursorVisible ? 1 : 0 }}>█</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Boot Phase — Apple logo + progress bar */}
            {phase === 'boot' && !webGLError && (
                <div style={styles.bootContainer}>
                    <AppleLogo style={styles.appleLogo} />
                    <div style={styles.progressTrack}>
                        <div style={{ ...styles.progressFill, width: `${progressBarWidth}%` }} />
                    </div>
                </div>
            )}

            {/* Login Phase */}
            {phase === 'login' && !webGLError && (
                <div style={{ ...styles.loginContainer, opacity: loginVisible ? 1 : 0 }}>
                    <AppleLogo style={styles.loginAppleLogo} />
                    <p style={styles.loginTitle}>Portfolio</p>
                    <p style={styles.loginSubtitle}>Sujal Kishore · Software Engineer</p>
                    {mobileWarning && (
                        <p style={styles.mobileWarn}>⚠ Best experienced on desktop</p>
                    )}
                    <button style={styles.loginButton} onClick={start}>
                        <span style={styles.loginButtonText}>Enter</span>
                        <span style={styles.loginButtonArrow}>→</span>
                    </button>
                </div>
            )}

            {/* WebGL Error */}
            {webGLError && (
                <div style={{ ...styles.loginContainer, opacity: webGLErrorOpacity }}>
                    <AppleLogo style={styles.loginAppleLogo} />
                    <p style={{ ...styles.loginTitle, color: '#ff453a' }}>No WebGL Detected</p>
                    <p style={styles.loginSubtitle}>
                        Please enable WebGL or switch to a compatible browser.
                    </p>
                </div>
            )}
        </div>
    );
};

const AppleLogo = ({ style }: { style: React.CSSProperties }) => (
    <div style={{ ...style, display: 'none' }} />
);

const styles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'opacity 0.4s ease, transform 0.4s ease',
        zIndex: 1000,
        backgroundColor: '#1a1a1a',
    },
    bgGradient: {
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(ellipse at 50% 40%, #1c1c2e 0%, #0d0d0d 80%)',
        pointerEvents: 'none',
    },

    /* ── macOS Terminal window ── */
    terminalWindow: {
        display: 'flex',
        flexDirection: 'column',
        width: 640,
        minHeight: 340,
        borderRadius: 12,
        overflow: 'hidden',
        background: 'rgba(30, 30, 30, 0.96)',
        boxShadow: '0 40px 100px rgba(0,0,0,0.8), 0 0 0 0.5px rgba(255,255,255,0.08)',
        zIndex: 1,
        backdropFilter: 'blur(20px)',
    },
    terminalTitlebar: {
        height: 38,
        background: '#2d2d2d',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 12,
        position: 'relative',
        flexShrink: 0,
    },
    trafficLights: {
        display: 'flex',
        gap: 7,
        alignItems: 'center',
    },
    tl: {
        width: 12,
        height: 12,
        borderRadius: '50%',
        border: '0.5px solid rgba(0,0,0,0.25)',
    },
    terminalTitle: {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: 13,
        fontWeight: 500,
        color: 'rgba(255,255,255,0.45)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
        letterSpacing: -0.2,
        userSelect: 'none',
    },
    terminalBody: {
        padding: '14px 18px 18px',
        fontFamily: '"SF Mono", "Menlo", "Monaco", "Consolas", "Liberation Mono", monospace',
        fontSize: 13,
        lineHeight: '1.7',
        color: '#d8d8d8',
        flex: 1,
        overflowY: 'auto',
    },
    terminalLine: {
        whiteSpace: 'pre',
        letterSpacing: 0,
    },
    emptyLine: {
        height: '1.7em',
    },
    terminalPromptRow: {
        display: 'flex',
        alignItems: 'baseline',
        marginTop: 2,
    },
    promptUser: {
        color: '#32d74b',
        fontFamily: '"SF Mono", "Menlo", "Monaco", monospace',
        fontSize: 13,
    },
    promptDir: {
        color: '#64d2ff',
        fontFamily: '"SF Mono", "Menlo", "Monaco", monospace',
        fontSize: 13,
    },
    terminalCursor: {
        color: '#d8d8d8',
        fontFamily: '"SF Mono", "Menlo", "Monaco", monospace',
        fontSize: 13,
        transition: 'opacity 0.05s',
    },

    /* ── Boot phase ── */
    bootContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 48,
        zIndex: 1,
    },
    appleLogo: {
        width: 72,
        height: 72,
        color: '#fff',
    },
    progressTrack: {
        width: 200,
        height: 4,
        borderRadius: 4,
        background: 'rgba(255,255,255,0.15)',
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
        borderRadius: 4,
        background: 'rgba(255,255,255,0.9)',
        transition: 'width 0.3s ease',
    },

    /* ── Login phase ── */
    loginContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 18,
        zIndex: 1,
        transition: 'opacity 0.5s ease',
        padding: 52,
        borderRadius: 28,
        minWidth: 360,

        background:
            'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))',

        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',

        border: '1px solid rgba(255,255,255,0.18)',

        boxShadow:
            '0 40px 120px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.15)',

        position: 'relative',
        overflow: 'hidden',
    },
    loginAppleLogo: {
        width: 80,
        height: 80,
        color: '#fff',
        marginBottom: 8,
    },
    loginTitle: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
        fontSize: 28,
        fontWeight: 600,
        color: '#fff',
        margin: 0,
        letterSpacing: -0.5,
    },
    loginSubtitle: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif',
        fontSize: 14,
        fontWeight: 400,
        color: 'rgba(255,255,255,0.6)',
        margin: 0,
        letterSpacing: 0,
    },
    mobileWarn: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif',
        fontSize: 12,
        fontWeight: 500,
        color: '#ffd60a',
        margin: 0,
        padding: '8px 16px',
        background: 'rgba(255, 214, 10, 0.1)',
        borderRadius: 8,
        border: '1px solid rgba(255, 214, 10, 0.2)',
    },
    loginButton: {
        marginTop: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 38px',
        borderRadius: 999,
        border: '1px solid rgba(255,255,255,0.35)',

        background:
            'linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.06))',

        backdropFilter: 'blur(30px)',
        WebkitBackdropFilter: 'blur(30px)',

        cursor: 'pointer',
        transition: 'all 0.25s ease',
        outline: 'none',

        boxShadow:
            '0 10px 30px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.3)',

        letterSpacing: 0.4,
    },
    loginButtonText: {
        fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif',
        fontSize: 15,
        fontWeight: 600,
        color: '#fff',
    },
    loginButtonArrow: {
        fontFamily: '-apple-system, sans-serif',
        fontSize: 18,
        color: 'rgba(255,255,255,0.9)',
    },
};

export default LoadingScreen;
